import { useRef, useMemo, useEffect } from "react";
import interactiveRegistry from "../../../Registry/Interactive/InteractiveRegistry";
import { useChartsStore } from "../../../store/chartStore";
import * as themeStoreModule from "../../../store/themeStore";
import { useSettingsStore } from "../../../store/settingsStore";
import { injectDatesIntoDrawing } from "./injectDatesIntoDrawing";

export default function useInteractiveObjects({
  chartSectionId,
  chartId,
  chartState,
} = {}) {
  // derive initial defs from the existing interactive registry (preferred)
  // Dynamically merge all categories from the registry
  const registryAll = useMemo(() => {
    const flattened = {};
    Object.values(interactiveRegistry || {}).forEach((category) => {
      if (category && typeof category === "object" && category.interactives) {
        Object.assign(flattened, category.interactives);
      }
    });
    return flattened;
  }, []);

  const initialDefsConfig = Object.values(registryAll).map((item) => ({
    type: item.key,
    // use registry `key` as the canonical storage slot
    objectsKey: item.key,
    propName: item.propName ?? item.key,
  }));

  // fallback if registry is empty (maintain backward compatibility)
  const fallbackDefs = [
    { type: "trendLine", objectsKey: "trendLine", propName: "trends" },
    {
      type: "fibRetracement",
      objectsKey: "fibRetracement",
      propName: "retracements",
    },
  ];
  const defsConfig = initialDefsConfig.length
    ? initialDefsConfig
    : fallbackDefs;

  // Get objects from chartStore instead of local state
  const getInteractiveObjects = useChartsStore(
    (state) => state.getInteractiveObjects,
  );
  const updateInteractiveObjects = useChartsStore(
    (state) => state.updateInteractiveObjects,
  );
  // Global active interactive state shared across all charts in the same workspace
  const activeInteractiveState = useChartsStore((state) =>
    state.getActiveInteractive(chartId),
  );
  const setActiveInteractiveInStore = useChartsStore(
    (state) => state.setActiveInteractive,
  );
  // reactive snapshot of the current chart's interactive objects so React re-renders
  // when the store changes for this chart. This fixes stale/memoized visuals while drawing.
  // Note: getInteractiveObjects now returns cached EMPTY_INTERACTIVE_OBJECTS to prevent infinite re-renders
  const currentObjects = useChartsStore((state) =>
    state.getInteractiveObjects(chartId, chartSectionId),
  );

  // generate stable refs for each configured interactive
  const generatedRefs = defsConfig.map(() => useRef(null));

  // Registry helpers - now update chartStore instead of local state
  const makeSetter = (key) => (valueOrUpdater) => {
    const currentObjects = getInteractiveObjects(chartId, chartSectionId) || {};
    const currentArray = currentObjects[key] || [];
    const newArray =
      typeof valueOrUpdater === "function"
        ? valueOrUpdater(currentArray)
        : valueOrUpdater;
    updateInteractiveObjects(chartId, key, newArray, chartSectionId);
  };

  // mutable defs so modules can register at runtime
  const defsRef = useRef(
    defsConfig.map((d, idx) => ({
      type: d.type,
      ref: generatedRefs[idx],
      objectsKey: d.objectsKey,
      propName: d.propName,
      setter: makeSetter(d.objectsKey),
    })),
  );

  // drawingObjectMap derived and kept in a ref so it's stable for consumers
  const drawingObjectMapRef = useRef(
    defsRef.current.reduce((acc, d) => {
      acc[d.type] = d.propName;
      return acc;
    }, {}),
  );

  const registerInteractive = ({ type, objectsKey, propName, ref }) => {
    // avoid duplicate registration
    if (defsRef.current.some((d) => d.type === type)) return;
    // ensure objects state has a slot in chartStore
    const currentObjects = getInteractiveObjects(chartId, chartSectionId) || {};
    if (!currentObjects[objectsKey]) {
      updateInteractiveObjects(chartId, objectsKey, [], chartSectionId);
    }
    defsRef.current.push({
      type,
      ref,
      objectsKey,
      propName,
      setter: makeSetter(objectsKey),
    });
    drawingObjectMapRef.current[type] = propName;
  };

  const continuousObjectInsertion = useSettingsStore(
    (state) => state.continuousObjectInsertion ?? false,
  );

  // active interactive type shared globally across all charts in the workspace
  // manage crosshair: force it on while in drawing mode and restore previous value on exit
  const getThemeState = themeStoreModule.useThemeStore.getState;
  const setCrosshairEnabled = getThemeState().setCrosshairEnabled;
  const _crosshairPrevRef = useRef(getThemeState().crosshairEnabled);

  // helper: clear all selection flags across all configured interactives
  const clearAllSelections = () => {
    defsRef.current.forEach((d) =>
      d.setter((prev) => prev.map((p) => ({ ...p, selected: false }))),
    );
  };

  // wrapped setter: when switching active interactive, ensure selections are cleared
  const setActiveInteractive = (typeOrNull) => {
    const currentActiveInteractive = useChartsStore
      .getState()
      .getActiveInteractive(chartId);

    // if switching to a specific interactive, clear selections from others
    if (typeOrNull) {
      // clear other selections, but leave the selected interactive's items as-is
      clearOtherSelections(typeOrNull);
      // entering drawing mode -> force crosshair on (remember previous)
      _crosshairPrevRef.current = getThemeState().crosshairEnabled;
      if (typeof setCrosshairEnabled === "function") setCrosshairEnabled(true);
    } else {
      // entering cursor/navigation mode -> clear all selections
      clearAllSelections();
      // only restore previous crosshair state if we were actually in drawing mode
      if (currentActiveInteractive) {
        // we were in drawing mode, restore previous state
        if (typeof setCrosshairEnabled === "function")
          setCrosshairEnabled(!!_crosshairPrevRef.current);
      }
      // if we weren't in drawing mode, don't change crosshair state at all
    }
    setActiveInteractiveInStore(chartId, typeOrNull);
  };

  // convenience lists/maps for consumers - memoize because defsRef.current is stable
  const registeredTypes = useMemo(() => defsRef.current.map((d) => d.type), []);

  const namedRefs = useMemo(
    () => Object.fromEntries(defsRef.current.map((d) => [d.type, d.ref])),
    [],
  );

  const getInteractiveNodes = () => {
    const nodes = [];
    defsRef.current.forEach((d) => {
      if (d.ref && d.ref.current)
        nodes.push({
          type: d.type,
          chartId: chartSectionId,
          node: d.ref.current,
        });
    });
    return nodes;
  };

  const clearOtherSelections = (activeType) => {
    defsRef.current.forEach((d) => {
      if (d.type !== activeType)
        d.setter((prev) => prev.map((p) => ({ ...p, selected: false })));
    });
  };

  const handleDrawingObjectSelect = (interactives) => {
    // find if any interactive reported a selection
    const active = interactives.find(
      (i) => Array.isArray(i.objects) && i.objects.some((o) => o.selected),
    );
    if (active) {
      const def = defsRef.current.find((d) => d.type === active.type);
      if (def) {
        def.setter((prev) =>
          prev.map((item, idx) => ({
            ...item,
            selected: !!(active.objects[idx] && active.objects[idx].selected),
          })),
        );
        clearOtherSelections(def.type);
        return;
      }
    }

    // no selection -> clear all
    defsRef.current.forEach((d) =>
      d.setter((prev) => prev.map((p) => ({ ...p, selected: false }))),
    );
  };

  const handleDrawingObjectDoubleClick = (item) => {
    const selectedIndex = item.index !== undefined ? item.index : 0;
    defsRef.current.forEach((d) => {
      if (d.type === item.type) {
        d.setter((prev) =>
          prev.map((p, idx) =>
            idx === selectedIndex
              ? { ...p, selected: true }
              : { ...p, selected: false },
          ),
        );
      } else {
        d.setter((prev) => prev.map((p) => ({ ...p, selected: false })));
      }
    });
  };

  const handleDrawingObjectContextMenu = (item, moreProps, e) => {
    // Default behavior: select the right-clicked object
    const selectedIndex = item.index !== undefined ? item.index : 0;
    defsRef.current.forEach((d) => {
      if (d.type === item.type) {
        d.setter((prev) =>
          prev.map((p, idx) =>
            idx === selectedIndex
              ? { ...p, selected: true }
              : { ...p, selected: false },
          ),
        );
      } else {
        d.setter((prev) => prev.map((p) => ({ ...p, selected: false })));
      }
    });

    // Additional contextmenu logic can be added here (e.g., show context menu, delete object, etc.)
    console.log("Right-clicked on drawing object:", item, e);
  };

  // Extract chart data for date injection (Issue #104 fix)
  const chartData = chartState?.data || null;

  const onCompleteFor = (type, newItems, options = {}) => {
    defsRef.current.forEach((d) => {
      if (d.type === type) {
        // Inject dates into each drawing for position persistence (Issue #104)
        // This ensures drawings maintain their position even when new bars are added
        const processedItems = newItems.map((it) => ({
          ...injectDatesIntoDrawing(it, chartData),
          selected: !!it.selected,
        }));
        d.setter(processedItems);
      } else {
        d.setter((prev) => prev.map((p) => ({ ...p, selected: false })));
      }
    });

    // After finishing a drawing, ensure the drawing is not left selected in the sidebar
    // and switch back to cursor/navigation mode. Do NOT remove the interactive from
    // the favorites list; just clear selection so the favorite appears inactive.
    // Exception: keepActive option allows tools like 'continuous' to stay active
    if (!options.keepActive) {
      setActiveInteractive(null);
    }
  };

  const onTrendComplete = (newTrends) => onCompleteFor("trendLine", newTrends);
  const onFibComplete = (newRetracements) =>
    onCompleteFor("fibRetracement", newRetracements);

  // build refs map keyed by type for consumers
  const refsMap = useMemo(
    () => Object.fromEntries(defsRef.current.map((d) => [d.type, d.ref])),
    [],
  );

  // build objects map keyed by type for consumers - now from chartStore
  const objectsMap = useMemo(() => {
    return Object.fromEntries(
      defsRef.current.map((d) => [d.type, currentObjects[d.objectsKey] || []]),
    );
  }, [currentObjects]);

  // build onComplete map keyed by type
  // 'continuous' tool always stays active; all others stay active when continuousObjectInsertion is ON
  const onCompleteMap = useMemo(
    () =>
      Object.fromEntries(
        defsRef.current.map((d) => {
          const keepActive =
            continuousObjectInsertion || d.type === "continuous";
          return [
            d.type,
            (items) => onCompleteFor(d.type, items, { keepActive }),
          ];
        }),
      ),
    [continuousObjectInsertion],
  );

  // onFinish callback for tools that need to explicitly end drawing mode (like continuous)
  const onFinishDrawing = useMemo(
    () => () => {
      setActiveInteractive(null);
    },
    [],
  );

  // Issue #319: when the continuous-insertion toggle flips (on→off or off→on),
  // deselect the currently active drawing tool. Without this, the tool stayed
  // active across the toggle change and the next click would still place one
  // extra object — surprising the user who expected the toggle to be a hard
  // stop. Symmetric behavior on both directions keeps the mental model simple:
  // changing modes = fresh start, pick the tool again.
  const _prevContinuousRef = useRef(continuousObjectInsertion);
  useEffect(() => {
    if (_prevContinuousRef.current !== continuousObjectInsertion) {
      _prevContinuousRef.current = continuousObjectInsertion;
      setActiveInteractive(null);
    }
  }, [continuousObjectInsertion]);

  // Extract displayXAccessor from chartState for coordinate conversion
  const displayXAccessor = chartState?.displayXAccessor || null;

  // Memoize the returned API object so consumers (parents) receive a stable
  // reference unless meaningful internal state (objects, activeInteractive)
  // changes. This prevents infinite update loops where parents set state
  // on every render because the API object identity changed.
  const api = useMemo(() => {
    return {
      trends: currentObjects.trendLine || [],
      retracements: currentObjects.fibRetracement || [],
      drawingObjectMap: drawingObjectMapRef.current,
      refsMap,
      objectsMap,
      onCompleteMap,
      onFinishDrawing,
      getInteractiveNodes,
      handleDrawingObjectSelect,
      handleDrawingObjectDoubleClick,
      handleDrawingObjectContextMenu,
      onTrendComplete,
      onFibComplete,
      registerInteractive,
      // toolbar/drawing mode helpers
      activeInteractive: activeInteractiveState,
      setActiveInteractive,
      registeredTypes,
      namedRefs,
      // Add chartId and chartSectionId for ActiveObjectsModal
      chartId,
      chartSectionId,
      // Add chart data and displayXAccessor for coordinate conversion
      chartData,
      displayXAccessor,
    };
  }, [
    chartId,
    chartSectionId,
    activeInteractiveState,
    refsMap,
    objectsMap,
    onCompleteMap,
    onFinishDrawing,
    registeredTypes,
    namedRefs,
    getInteractiveObjects,
    chartData,
    displayXAccessor,
  ]);

  return api;
}
